import { makeStyles, Typography, useMediaQuery } from "@material-ui/core";
import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import FullPageError from "./FullPageError";
import { Skeleton } from "@material-ui/lab";
import CircularProgress from "./CircularProgress";

const useStyles = makeStyles((theme) => ({
  overlay: {
    width: "100%",
    height: "24px",
    background: "#002e38",
    position: "absolute",
    right: 0,
    bottom: 0,
    cursor: "default",
  },
}));
const ResponsiveIframe = ({
  src,
  minWidth,
  apiKeyFunc = null,
  srcParamsError = null,
  srcParams = null,
  srcParamsRequired = false,
  showhideOverlay = false,
  iframeLoadTimeout = 8000,
}) => {
  const classes = useStyles();
  const [failed, setFailed] = useState(Boolean(!src));
  const [isLoading, setIsLoading] = useState(Boolean(src));
  const [canRenderIframe, setCanRenderIframe] = useState(false);
  const [displayHideOverlay, setDisplayHideOverlay] = useState(false);

  const iframeRef = useRef(null);

  // Pre-flight check: is the iframe URL reachable?
  // This solution is needed because in Chrome onLoad event fires even when iframe loading fails
  // it then loads chrome-error:// document inside iframe
  useEffect(() => {
    let mounted = true;

    const checkReachable = async (url) => {
      try {
        await fetch(url, { method: "HEAD", mode: "no-cors" });
        return true;
      } catch {
        return false;
      }
    };

    (async () => {
      if (!src) {
        setFailed(true);
        return;
      }

      const ok = await checkReachable(src);
      if (!mounted) return;

      if (!ok) {
        setFailed(true);
        setIsLoading(false);
      } else {
        setCanRenderIframe(true);
        setIsLoading(true);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [src]);

  // Handling of iframes with required params such as looker studio iframe
  const fullSrc = useMemo(() => {
    if (!src) return null;
    if (srcParamsRequired) {
      return srcParams ? `${src}?params=${srcParams}` : null;
    }
    return src;
  }, [src, srcParamsRequired, srcParams]);
  const fullSrcReady = useMemo(() => {
    if (!srcParamsRequired) {
      return Boolean(src);
    }
    return Boolean(srcParams) && Boolean(src) && !srcParamsError;
  });
  // Timeout fallback in case iframe never loads
  useEffect(() => {
    if (!canRenderIframe) return;

    const timer = setTimeout(() => {
      if (isLoading) {
        setFailed(true);
        setIsLoading(false);
      }
    }, iframeLoadTimeout);

    return () => clearTimeout(timer);
  }, [isLoading, canRenderIframe, iframeLoadTimeout]);

  const handleLoad = () => {
    if (apiKeyFunc) {
      apiKeyFunc(iframeRef);
    }
    setIsLoading(false);
    let overlayTimer;
    if (showhideOverlay) {
      overlayTimer = setTimeout(() => {
        setDisplayHideOverlay(true);
      }, 1500);
    }
    return () => {
      if (showhideOverlay && overlayTimer) {
        clearTimeout(overlayTimer);
      }
    };
  };

  const handleError = () => {
    setFailed(true);
    setIsLoading(false);
  };

  const displayLoadingOverlay = useMemo(() => {
    return fullSrcReady && isLoading && !failed;
  }, [isLoading, failed, fullSrcReady]);
  const displayErrorOverlay = useMemo(() => {
    return !displayLoadingOverlay && (failed || srcParamsError);
  }, [failed, displayLoadingOverlay, srcParamsError]);
  const displayIFrame = useMemo(() => {
    return canRenderIframe && !failed && !srcParamsError && fullSrcReady;
  }, [canRenderIframe, failed, fullSrcReady, srcParamsError]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100vh",
        overflowX: "auto",
      }}
    >
      {/* Iframe container */}
      {displayIFrame && (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100vh",
            minWidth,
          }}
        >
          <iframe
            ref={iframeRef}
            src={fullSrc}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
              visibility: isLoading ? "hidden" : "visible",
            }}
            title="Responsive Iframe"
            onLoad={handleLoad}
            onError={handleError}
          />
          {displayHideOverlay && (
            <div
              className={classes.overlay}
              onMouseEnter={(e) => e.stopPropagation()}
              onMouseMove={(e) => e.stopPropagation()}
              onMouseLeave={(e) => e.stopPropagation()}
              onFocusCapture={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={() => {
                if (document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
              }}
            ></div>
          )}
        </div>
      )}

      {/* Loading overlay */}
      {displayLoadingOverlay && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "white",
          }}
        >
          <CircularProgress />
          <Typography variant="subtitle1" color="textSecondary">
            Dashboard loading...
          </Typography>
          <Skeleton
            variant="rounded"
            width="90%"
            height="50%"
            style={{ marginTop: 20 }}
          />
        </div>
      )}

      {/* Error overlay */}
      {displayErrorOverlay && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "white",
          }}
        >
          <FullPageError />
        </div>
      )}
    </div>
  );
};

export default memo(ResponsiveIframe);
